/**
 * CommandHistoryTable - Tabla de historial de comandos enviados a una controladora
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Terminal,
  RefreshCw,
} from 'lucide-react';
import { controllersService } from '@/services';
import type { CommandType, CommandStatus } from '@/types/api';

interface CommandHistoryTableProps {
  controllerId: string;
  controllerName?: string;
}

const commandTypeLabels: Record<CommandType, string> = {
  valve_open: 'Abrir válvula',
  valve_close: 'Cerrar válvula',
  valve_timed: 'Abrir válvula (tiempo)',
  reboot: 'Reiniciar',
  set_schedule: 'Configurar programación',
  set_timezone: 'Configurar zona horaria',
  sync_time: 'Sincronizar hora',
  reset_counter: 'Resetear contador',
  other: 'Otro',
};

const statusColors: Record<CommandStatus, string> = {
  queued: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  sent: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  received: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  executed: 'bg-green-500/10 text-green-600 border-green-500/20',
  failed: 'bg-red-500/10 text-red-600 border-red-500/20',
  timeout: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  cancelled: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

const statusIcons: Record<CommandStatus, React.ReactNode> = {
  queued: <Clock className="w-3.5 h-3.5" />,
  sent: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
  received: <RefreshCw className="w-3.5 h-3.5" />,
  executed: <CheckCircle2 className="w-3.5 h-3.5" />,
  failed: <XCircle className="w-3.5 h-3.5" />,
  timeout: <AlertCircle className="w-3.5 h-3.5" />,
  cancelled: <XCircle className="w-3.5 h-3.5" />,
};

export function CommandHistoryTable({ controllerId, controllerName }: CommandHistoryTableProps) {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [typeFilter, setTypeFilter] = useState<CommandType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<CommandStatus | 'all'>('all');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const { data: commandsData, isLoading } = useQuery({
    queryKey: ['controller-commands', controllerId, page, limit, typeFilter, statusFilter, startDate, endDate],
    queryFn: async () => {
      const params: any = {
        page,
        limit,
        startDate,
        endDate,
      };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await controllersService.getCommands(controllerId, params);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar comandos');
      }
      return response.data;
    },
  });

  const { data: statsData } = useQuery({
    queryKey: ['controller-commands-stats', controllerId],
    queryFn: async () => {
      const response = await controllersService.getCommandsStats(controllerId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar estadísticas');
      }
      return response.data;
    },
  });

  const commands = commandsData?.items || [];
  const pagination = commandsData?.pagination;
  const stats = statsData?.data;

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Total comandos</div>
              <div className="text-2xl font-bold">{stats.totalCommands}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Exitosos</div>
              <div className="text-2xl font-bold text-green-600">{stats.successfulCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Fallidos</div>
              <div className="text-2xl font-bold text-red-600">{stats.failedCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Pendientes</div>
              <div className="text-2xl font-bold text-yellow-600">{stats.pendingCount}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Tipo de comando</Label>
              <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v as CommandType | 'all'); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(commandTypeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as CommandStatus | 'all'); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.keys(statusColors).map((status) => (
                    <SelectItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Desde</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              />
            </div>
            <div className="space-y-2">
              <Label>Hasta</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historial de comandos</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : commands.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No hay comandos registrados en el período seleccionado
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha/Hora</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Válvula</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Duración</TableHead>
                      <TableHead>Origen</TableHead>
                      <TableHead>Payload</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commands.map((command) => (
                      <TableRow key={command.id}>
                        <TableCell className="font-mono text-xs">
                          {format(parseISO(command.createdAt), 'dd/MM/yyyy HH:mm:ss', { locale: es })}
                        </TableCell>
                        <TableCell>
                          {commandTypeLabels[command.commandType] || command.commandType}
                        </TableCell>
                        <TableCell>
                          {command.valveName ? (
                            <span className="text-sm">
                              {command.valveName} (Canal {command.valveChannel})
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn('gap-1', statusColors[command.status])}
                          >
                            {statusIcons[command.status]}
                            {command.status.charAt(0).toUpperCase() + command.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {command.durationSeconds ? (
                            <span className="text-sm">{command.durationSeconds}s</span>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {command.triggeredBy}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="flex items-center gap-1 text-xs font-mono text-muted-foreground">
                                <Terminal className="w-3 h-3" />
                                {command.payloadHex.substring(0, 8)}...
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="font-mono text-xs">
                                <div>Enviado: {command.payloadHex}</div>
                                {command.responsePayloadHex && (
                                  <div>Respuesta: {command.responsePayloadHex}</div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {((page - 1) * limit) + 1} - {Math.min(page * limit, pagination.total)} de {pagination.total}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                      disabled={page === pagination.totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
